import { cn } from "@/shared/lib/utils";
import TransparentButton from "@/shared/ui/buttons/TransparentButton";
import Typography from "@/shared/ui/Typography";
import * as React from "react";
import { IMaskInput } from "react-imask";

import Phone from "@/assets/icons/phone/black.svg?react";
import PhoneWhite from "@/assets/icons/phone/white.svg?react";
import Whatsapp from "@/assets/icons/social/whatsapp.svg?react";
import Telegram from "@/assets/icons/social/telegram.svg?react";
import DownloadIcon from "@/assets/icons/house/houseDownloadBlack.svg?react";
import DownloadWhiteIcon from "@/assets/icons/house/houseDownloadWhite.svg?react";
import { useAppSelector } from "@/app/store/hooks";
import { selectContacts } from "@/entities/site_data/model/selects";

export interface IFeedbackFormProps {
    className?: string;
    download?: boolean;
    bgBlue?: boolean;
    onSubmit?: (name: string, phone: string) => void;
}

const btnStyles =
    "flex flex-row justify-center items-center gap-[12px] bg-white-50 w-full h-[47px] font-normal text-[14px] md:text-[16px] px-[20px] whitespace-nowrap";
const socialBtnStyles =
    "shadow-glass border-[0.5px] border-white-50 !leading-none rounded-full font-manrope cursor-pointer transition-all ease-linear duration-200";
const inputStyle =
    "shadow-glass bg-white-10 w-full h-[46px] md:h-[48px] font-manrope rounded-full outline-none px-[20px] font-medium text-[14px] md:text-[16px]";

const colorVariants = {
            black: "bg-white-50 hover:bg-white-80 active:bg-white-20 text-black",
            white: "bg-white-20 hover:bg-white-50 active:bg-white-10 text-white"
        };

export function FeedbackForm({
    className,
    download,
    bgBlue,
    onSubmit,
}: IFeedbackFormProps) {
    const [formData, setFormData] = React.useState({
        name: "",
        tel: "",
        policy: false,
    });

    const btsWrapRef = React.useRef<HTMLDivElement>(null);

    const contacts = useAppSelector(selectContacts);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (onSubmit && formData.policy) {
            onSubmit(formData.name, formData.tel);

            setFormData({
                name: "",
                tel: "",
                policy: false,
            })
        }
            

    };

    const handleContactBtn = (link?: string) => {
        if(link)
            window.open(link, "_blank", "noopener,noreferrer");
    };

    React.useEffect(() => {
        const btnsReformer = () => {
            if (window.innerWidth <= 768 && btsWrapRef.current) {
                const el = btsWrapRef.current;
                const centerScrollLeft = (el.scrollWidth - el.clientWidth) / 2;

                el.scrollTo({
                    left: centerScrollLeft,
                    behavior: "smooth",
                });
            }
        };

        btnsReformer();
        window.addEventListener("resize", btnsReformer);

        return () => {
            window.removeEventListener("resize", btnsReformer);
        };
    }, []);

    return (
        <form
            action={""}
            className={cn(
                "flex flex-col gap-[8px] md:gap-[16px] lg:flex-row",
                className
            )}
            onSubmit={handleSubmit}
        >
            <div className="flex flex-col gap-[8px] md:gap-[16px] lg:w-[450px]">
                <input
                    className={cn(
                        inputStyle,
                        bgBlue ? "text-white-50" : "text-black-50"
                    )}
                    placeholder="Имя"
                    value={formData.name}
                    onChange={(e) =>
                        setFormData((prev) => ({
                            ...prev,
                            name: e.target.value,
                        }))
                    }
                    minLength={2}
                    maxLength={50}
                    required
                    type="text"
                />

                <IMaskInput
                    className={cn(
                        inputStyle,
                        formData.tel.length < 11 && "text-red-error-60",
                        bgBlue ? "text-white-50" : "text-black-50"
                    )}
                    mask="+{7} (000) 000-00-00"
                    unmask={true}
                    value={formData.tel}
                    onAccept={(value) =>
                        setFormData((prev) => ({ ...prev, tel: value }))
                    }
                    placeholder="+7 (___) ___-__-__"
                    required
                    type="tel"
                />

                <div className="flex flex-row gap-[10px] items-center">
                    <input
                        className={cn(
                            "min-w-[35px] min-h-[35px] relative appearance-none border rounded-[10px] bg-transparent cursor-pointer flex justify-center items-center transition-colors",
                            bgBlue
                                ? "border-white checked:bg-white"
                                : "border-black checked:bg-black",
                            "checked:after:content-['✔'] checked:after:absolute checked:after:text-lg checked:after:font-bold",
                            bgBlue
                                ? "checked:after:text-blue-600"
                                : "checked:after:text-white"
                        )}
                        type="checkbox"
                        required
                        checked={formData.policy}
                        onChange={() =>
                            setFormData((prev) => ({
                                ...prev,
                                policy: !prev.policy,
                            }))
                        }
                    />

                    <label>
                        <Typography
                            tag="span"
                            className={cn(
                                "block font-normal text-[10px] !leading-[120%]",
                                bgBlue ? "text-white" : "text-black"
                            )}
                        >
                            Я полностью ознакомился и согласен с
                            Пользовательским соглашением,
                            <a
                                href="/privacy-policy"
                                className="underline cursor-pointer"
                            >
                                Политикой обработки персональных данных и файлов
                                Cookie
                            </a>
                            , и предоставляю конкретное, предметное,
                            информированное, сознательное и однозначное Согласие
                            на обработку моих персональных данных.
                        </Typography>
                    </label>
                </div>
            </div>

            <div className={cn("flex flex-col gap-[16px] lg:w-auto lg:flex-1")}>
                <TransparentButton
                    className={cn(
                        btnStyles,
                        "hidden md:flex",
                        bgBlue ? "text-white bg-white-20" : "text-black"
                    )}
                    textColor={bgBlue? 'white': 'black'}
                >
                    {download ? "Скачать презентацию" : "Заказать звонок"}
                    {download ? (
                        bgBlue?<DownloadWhiteIcon className="w-[20px] h-[23px]" />: <DownloadIcon className="w-[20px] h-[23px]" />
                    ) : bgBlue ? (
                        <PhoneWhite className="w-[23px] h-[23px]" />
                    ) : (
                        <Phone className="w-[23px] h-[23px]" />
                    )}
                </TransparentButton>

                <div
                    ref={btsWrapRef}
                    className={cn(
                        "flex flex-row gap-[8px] md:gap-[16px] w-full overflow-x-auto scrollbar-hide"
                    )}
                >
                    <div
                        className={cn(
                            btnStyles,
                            socialBtnStyles,
                            colorVariants[bgBlue? 'white': 'black'],
                            "md:w-1/2",
                            bgBlue ? "text-white bg-white-20" : "text-black"
                        )}
                        onClick={() => handleContactBtn(contacts?.telegramUrl)}
                    >
                        Связаться в Telegram
                        <Telegram className="w-[23px] h-[23px]"/>
                    </div>

                    <TransparentButton
                        className={cn(
                            btnStyles,
                            "md:hidden",
                            bgBlue ? "text-white bg-white-20" : "text-black"
                        )}
                        textColor={bgBlue? 'white': 'black'}
                    >
                        {download ? "Скачать презентацию" : "Заказать звонок"}
                        {download ? (
                            bgBlue?<DownloadWhiteIcon className="w-[20px] h-[23px]" />: <DownloadIcon className="w-[20px] h-[23px]" />
                        ) : bgBlue ? (
                            <PhoneWhite className="w-[23px] h-[23px]" />
                        ) : (
                            <Phone className="w-[23px] h-[23px]" />
                        )}
                    </TransparentButton>

                    <div
                        className={cn(
                            btnStyles,
                            socialBtnStyles,
                            colorVariants[bgBlue? 'white': 'black'],
                            "md:w-1/2 ",
                            bgBlue ? "text-white bg-white-20" : "text-black"
                        )}
                        onClick={() => handleContactBtn(contacts?.whatsappUrl)}
                    >
                        Связаться в WhatsApp
                        <Whatsapp className="w-[23px] h-[23px]" />
                    </div>
                </div>
            </div>
        </form>
    );
}
